import { isAuthDisconnected, notifyAuthDisconnected } from '@/lib/auth/authDisconnect';

const INSTALLED_FLAG = '__PACMASTER_BFF_DISCONNECT_FETCH_INTERCEPTOR__';

type FetchArgs = Parameters<typeof fetch>;

type DisconnectTrigger = {
  reason: 'unauthenticated' | 'forbidden' | 'unknown';
  status?: number;
  url?: string;
  detail?: string;
};

// ─────────────────────────────────────────────────────────────────────────
// Session-liveness contract (RFC 6750 § 3.1).
//
// CONTRACT
// --------
// HTTP 401 alone never means "your session is dead" — it just means
// "this resource was unauthorized for this request". The BFF is the only
// entity that can decide if a 401 implies the session itself is gone.
// It signals that with the standard WWW-Authenticate header:
//
//   401 Unauthorized
//   WWW-Authenticate: Bearer error="invalid_token", error_description="..."
//
// The interceptor below tears down the SPA session ONLY when both
// conditions hold. New routes are therefore default-safe: a 401 from
// any route that doesn't explicitly opt in via WWW-Authenticate will
// surface as a per-feature error, not a global logout.
//
// STATUS PROBES (soft endpoints) — `/auth/linked-identities`, etc. —
// never return 401 by design; they return
// `200 + { degraded: true, reason }`. So we never see them here.
//
// Forbidden (403) is always per-resource (caller lacks permission).
// Never disconnect on 403.
// ─────────────────────────────────────────────────────────────────────────

function isBffUrl(url: URL): boolean {
  // Only consider same-origin /bff/* routes as "local" BFF traffic.
  // Cross-origin fetches to a remote BFF (e.g. device-auth push to
  // another Cloud Run instance) must NOT trigger local session logout.
  if (typeof window !== 'undefined' && url.origin !== window.location.origin) {
    return false;
  }
  return url.pathname === '/bff' || url.pathname.startsWith('/bff/');
}

function isActiveAuthFlow(url: URL): boolean {
  // The login + refresh endpoints CAN return 401 with WWW-Authenticate
  // (e.g. wrong password, expired refresh). Those are normal control-flow
  // signals for the FE, not "the session you were just using is dead".
  // Treat them as opt-out from the global disconnect cascade so the
  // login/refresh UI can surface its own errors without the interceptor
  // double-redirecting on top of it.
  const p = url.pathname;
  return p.endsWith('/auth/login') || p.endsWith('/auth/refresh');
}

/**
 * Parse RFC 6750 WWW-Authenticate header and return the `error` token if
 * the scheme is Bearer. Returns null when absent or malformed.
 *
 * Examples we handle:
 *   Bearer realm="pacmaster", error="invalid_token", error_description="session_expired"
 *   Bearer error="invalid_token"
 *
 * Examples we do NOT treat as session loss:
 *   Bearer realm="pacmaster"                   (no error token)
 *   Basic realm="..."                          (wrong scheme)
 */
function parseWwwAuthenticate(header: string | null): { error: string; description?: string } | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (!/^bearer\b/i.test(trimmed)) return null;
  const errMatch = trimmed.match(/\berror\s*=\s*"([^"]+)"/i);
  if (!errMatch) return null;
  const descMatch = trimmed.match(/\berror_description\s*=\s*"([^"]+)"/i);
  return { error: errMatch[1], description: descMatch?.[1] };
}

function classify(res: Response, url: URL): DisconnectTrigger | null {
  if (isActiveAuthFlow(url)) return null;

  // 403 is always per-resource (lacks permission); never disconnect.
  if (res.status !== 401) return null;

  const wwwAuth = parseWwwAuthenticate(res.headers.get('WWW-Authenticate'));
  // Only `invalid_token` triggers session disconnect. `insufficient_scope`
  // and other RFC 6750 errors mean the session is fine but this specific
  // call needs different scopes.
  if (wwwAuth?.error !== 'invalid_token') return null;

  return {
    reason: 'unauthenticated',
    status: 401,
    url: url.pathname,
    detail: wwwAuth.description,
  };
}

function safeUrlFromFetchArgs(input: FetchArgs[0], init?: FetchArgs[1]): URL | null {
  try {
    const base = typeof window !== 'undefined' ? window.location.href : 'http://localhost/';

    // Handle string URLs
    if (typeof input === 'string') return new URL(input, base);

    // Handle Request objects
    if (typeof Request !== 'undefined' && input instanceof Request) {
      return new URL(input.url, base);
    }

    // Some code passes { url: string }
    const maybeUrl = (input as any)?.url;
    if (typeof maybeUrl === 'string') return new URL(maybeUrl, base);

    // init can contain a URL in rare wrappers
    const initUrl = (init as any)?.url;
    if (typeof initUrl === 'string') return new URL(initUrl, base);

    return null;
  } catch {
    return null;
  }
}

export function installBffDisconnectFetchInterceptor() {
  const g: any = globalThis as any;
  if (g[INSTALLED_FLAG]) return;
  g[INSTALLED_FLAG] = true;

  if (typeof fetch !== 'function') return;

  const originalFetch = fetch.bind(globalThis);

  // Wrap fetch and emit auth-disconnect events for any /bff/* traffic,
  // including code paths that do NOT use the shared bff client.
  (globalThis as any).fetch = async (...args: FetchArgs) => {
    const url = safeUrlFromFetchArgs(args[0], args[1]);
    const isBff = url ? isBffUrl(url) : false;

    try {
      const res = await originalFetch(...args);

      if (isBff && url) {
        const trigger = classify(res, url);
        if (trigger && !isAuthDisconnected()) notifyAuthDisconnected(trigger);
      }

      return res;
    } catch (err) {
      // Network failure: do NOT force logout.
      throw err;
    }
  };
}

// Exposed for unit/integration tests so the contract can be exercised
// without installing the global fetch interceptor.
export const __testing__ = { parseWwwAuthenticate, classify };
